/**
 * Which of a role's `forms.fields` entries are worth showing a sponsor in a data grid.
 *
 * `{eventId}_forms.fields` is not purely a description of the form. Events also list the
 * payment and bookkeeping columns there — amount, currency, payment_status, payment_id,
 * product_name and friends — because other tools use that list as "the columns this role has".
 * Rendering it verbatim gives a sponsor a grid whose first screenful is machine bookkeeping
 * rather than the people they registered.
 *
 * These columns are hidden, not removed: they stay in the underlying row (and in Export), so
 * nothing that reads the data is affected — this only decides what is on screen by default.
 */

/** Column names that are system bookkeeping, never something a person typed into the form. */
const SYSTEM_FIELDS = new Set(
  [
    // payment / gateway
    'amount',
    'currency',
    'payment_status',
    'payment_id',
    'paymentmethod',
    'order_id',
    'product_name',
    // sponsor + link plumbing
    'ser_id',
    'stallno',
    'reglink',
    'link_url',
    'communicationdecision',
    'regdevice',
    // review / verification bookkeeping
    'status',
    'documents',
    'moved_id',
    'sponsor_id',
    'decided_at',
    'decided_by',
    // identity handled by dedicated columns already
    'serial',
  ].map((s) => s.toLowerCase()),
);

/** True when this field is bookkeeping rather than something the registrant filled in. */
export function isSystemField(name) {
  return SYSTEM_FIELDS.has(String(name ?? '').toLowerCase());
}

/**
 * The form fields a sponsor should see as grid columns: what the registrant actually filled in.
 * Falls back to an empty list when fields are absent, so a caller can spread it safely.
 */
export function visibleFormFields(fields) {
  return (fields || []).filter((f) => f && f.name && !isSystemField(f.name));
}

/**
 * Format a grid `created` cell.
 *
 * Sources date differently — the role tables return epoch-milliseconds strings, the review queue
 * an ISO timestamp — and date-fns `format()` THROWS on an invalid date, which takes down the whole
 * DataGrid ("Invalid time value") instead of spoiling one cell. Accept both, never throw.
 */
export function formatGridDate(value, format, pattern = 'dd-MMM-yyyy hh:mm a') {
  if (value == null || value === '') return '';
  const d = /^\d+$/.test(String(value).trim()) ? new Date(Number(value)) : new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : format(d, pattern);
}
